import {combine, createEffect, createEvent, createStore} from 'effector';

import {authorize, authorizeWithCert, deauthorize, fetchProfile} from 'api';
import {createOrganization} from 'api/registration';
import {TokenStorage} from 'core/tokenStorage';
import {Profile, UserPermission, UserRoles} from 'types';
import {CertData} from 'types/plugins/cadesPlugin';

import {findPermissions} from 'utils/roles';

export const loadProfileFx = createEffect({
    name: 'loadSessionUserFx',
    handler: fetchProfile,
});
export const loginFx = createEffect({
    name: 'loginFx',
    handler: authorize,
});
export const loginWithCertFx = createEffect({
    name: 'loginWithCertFx',
    handler: authorizeWithCert,
});
export const logoutFx = createEffect({
    name: 'logoutFx',
    handler: deauthorize,
});
export const cleanAuthDataFx = createEffect({
    name: 'cleanAuthDataFx',
    handler: () => TokenStorage.removeTokens(),
});

export const createOrganizationFx = createEffect({
    name: 'createOrganizationFx',
    handler: createOrganization,
});

export const reloadPageFx = createEffect(() => window.location.reload());

export const doLogin = createEvent<{login: string; password: string; saveCredentials: boolean}>();
export const doLoginWithCert = createEvent<{cert: CertData; id: number}>();
export const doLogout = createEvent<boolean>();
export const cleanAuthData = createEvent();

export const profile$ = createStore<Profile | null>(null);
export const isAuth$ = profile$.map((user) => !!user);
export const isOperator$ = profile$.map((user) =>
    user?.permissions.includes(UserPermission.PERMISSION_MPL_OPERATOR_ADDITIONAL_TABLES_DATA),
);
export const isDirector$ = profile$.map((user) =>
    user?.roles.some((role) =>
        [
            UserRoles.ROLE_MPL_DISTRIBUTOR_DIRECTOR,
            UserRoles.ROLE_MPL_MANUFACTURER_DIRECTOR,
            UserRoles.ROLE_MPL_IMPORTER_DIRECTOR,
            UserRoles.ROLE_MPL_RETAIL_DIRECTOR,
            UserRoles.ROLE_MPL_TS_DIRECTOR,
        ].includes(role),
    ),
);
export const isAdmin$ = profile$.map((user) =>
    user?.roles.includes(UserRoles.ROLE_MPL_OPERATOR_ADMIN),
);

export const isCustomsRole$ = profile$.map((user) =>
    user?.roles.some((role) =>
        [UserRoles.ROLE_MPL_TS_DIRECTOR, UserRoles.ROLE_MPL_TS_EMPLOYEE].includes(role),
    ),
);

export const isRetailerRole$ = profile$.map((user) =>
    user?.roles.some((role) =>
        [UserRoles.ROLE_MPL_RETAIL_DIRECTOR, UserRoles.ROLE_MPL_RETAIL_EMPLOYEE].includes(role),
    ),
);

export const permissions$ = profile$.map((user) => findPermissions(user?.roles[0]));
export const $mainRole = profile$.map((user) => user?.roles[0]);

export const profileState$ = combine({
    profile: profile$,
    loading: loadProfileFx.pending,
    isDirector: isDirector$,
});

export const authSyncReceived = createEvent<'LOGIN' | 'LOGOUT'>();

export const loginError$ = createStore<string | null>(null);

export const loginRedirectFromPrevPath$ = createStore<boolean>(true);
